import type { ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { useLoaderData, useRevalidator } from 'react-router'
import { Button, RawButton } from '@/components/Button'
import { ContentError } from '@/components/ContentError'
import { Dropdown } from '@/components/Dropdown'
import type { IconName } from '@/components/Icon'
import { Segmented } from '@/components/Segmented'
import { AutoSkeleton, SkeletonList } from '@/components/Skeleton'
import { ViewHeading } from '@/components/ViewHeading'
import { useDocumentTitle } from '@/hooks/document-title'
import { useHotkey } from '@/hooks/keyboard-hooks'
import { useActivation } from '@/hooks/view-model-hooks'
import { useHideGlobalNavigation } from '@/features/shell/global-navigation/Sidebar/SidebarContainer'
import {
  HydrationErrorBanner,
  LegendEditor,
  LegendEditorHeader,
  OlderVersionBanner,
  PaletteButton,
  SaveStatus,
} from '@/features/legends/LegendEditor'
import type { LegendEditorViewLoaderData } from '@/features/legends/legend-route-loaders'
import { SidebarLayout } from '@/features/legends/components/SidebarLayout'
import { SidebarMenuItemSkeleton } from '@/features/legends/components/SidebarMenu'
import {
  OptionsTabContainer,
  OptionsTabSkeletonContainer,
} from '@/features/legends/options/components/OptionsTabContainer'
import {
  PreviewInputsPane,
  PreviewLayout,
  PreviewOutputsPane,
} from '@/features/legends/preview/components/PreviewLayout'
import {
  PreviewQuestionRowSkeleton,
  PreviewQuestions,
} from '@/features/legends/preview/components/PreviewQuestions'
import { PreviewTabContainer } from '@/features/legends/preview/components/PreviewTabContainer'
import {
  QuestionsTabContainer,
  QuestionsTabSkeletonContainer,
} from '@/features/legends/questions/components/QuestionsTabContainer'
import { SymbolPaletteContainer } from '@/features/legends/questions/components/SymbolPalette/SymbolPaletteContainer'
import { TemplatesSidebar } from '@/features/legends/templates/components/TemplatesSidebar'
import { TemplatesTabContainer } from '@/features/legends/templates/components/TemplatesTabContainer'
import { ConflictDialogContainer } from '@/features/legends/versioning/ConflictDialog/ConflictDialogContainer'
import { RevertDialogContainer } from '@/features/legends/versioning/RevertDialog/RevertDialogContainer'
import { PublishPanelContainer } from '@/features/legends/versioning/components/PublishPanel/PublishPanelContainer'
import { VersionControls } from '@/features/legends/versioning/components/VersionControls'
import { VersionPickerContainer } from '@/features/legends/versioning/components/VersionPicker/VersionPickerContainer'
import type { HeaderTab, LegendEditorViewModel } from './LegendEditorViewModel'

const headerTabs: { tab: HeaderTab; title: string; icon: IconName }[] = [
  { tab: 'Options', title: 'Options', icon: 'sliders' },
  { tab: 'Questions', title: 'Questions', icon: 'list-view' },
  { tab: 'Templates', title: 'Templates', icon: 'documents' },
  { tab: 'Preview', title: 'Preview', icon: 'view' },
]

const isApplePlatform = /Mac|iPhone|iPad/.test(navigator.userAgent)

const testId = 'legend-editor'

export default observer(function LegendEditorContainer() {
  const data = useLoaderData<LegendEditorViewLoaderData>()

  return data && <LegendEditorViewContainer vm={data.vm} />
})

interface LegendEditorContainerProps {
  vm: LegendEditorViewModel
}

const LegendEditorViewContainer = observer(function LegendEditorViewContainer({
  vm,
}: LegendEditorContainerProps) {
  const { revalidate } = useRevalidator()
  // If the legend fails to load, the navigation stays so there's a way out.
  useHideGlobalNavigation(!vm.fetch.rejected)
  useDocumentTitle(vm.name || 'Legend')
  useActivation(vm)
  useHotkey([isApplePlatform ? 'Meta' : 'Ctrl', 'KeyK'], (e) => {
    e.preventDefault()
    vm.togglePalette()
  })

  return vm.fetch.match({
    pending: () => <LegendEditorSkeletonContainer vm={vm} />,
    rejected: (err) => (
      <ContentError
        title="Loading the legend failed"
        error={err}
        onRetry={revalidate}
      />
    ),
    resolved: () => <LegendEditorContentContainer vm={vm} />,
  })
})

/**
 * The editor's header and tabs, with a skeleton of the current tab below.
 */
const LegendEditorSkeletonContainer = observer(
  function LegendEditorSkeletonContainer({ vm }: LegendEditorContainerProps) {
    return (
      <LegendEditor
        header={
          <LegendEditorHeader
            isViewingOlderVersion={false}
            breadcrumb={
              <LegendEditorBreadcrumbContainer
                vm={vm}
                name={
                  <AutoSkeleton
                    isSkeleton={vm.loadingName === null}
                    characters={16}
                  >
                    {vm.loadingName}
                  </AutoSkeleton>
                }
              />
            }
            versionControls={null}
            tabs={<LegendEditorTabsContainer vm={vm} isSkeleton />}
            actions={null}
          />
        }
        testId={testId}
      >
        <LegendEditorTabSkeletonContainer vm={vm} />
      </LegendEditor>
    )
  },
)

const LegendEditorContentContainer = observer(
  function LegendEditorContentContainer({ vm }: LegendEditorContainerProps) {
    const versionsVm = vm.versionsVm
    return (
      <LegendEditor
        header={<LegendEditorHeaderContainer vm={vm} />}
        banner={
          <>
            {!versionsVm.isViewingLatestVersion && vm.legendVersion && (
              <OlderVersionBanner
                version={vm.legendVersion.version}
                isLive={versionsVm.isViewingLiveVersion}
                isPending={versionsVm.showLatestVersion.pending}
                onShowLatest={versionsVm.showLatestVersion}
              />
            )}
            {vm.hydrationError !== null && (
              <HydrationErrorBanner error={vm.hydrationError} />
            )}
          </>
        }
        dialogs={
          <>
            <SymbolPaletteContainer vm={vm} />
            <ConflictDialogContainer vm={versionsVm.conflictVm} />
            <RevertDialogContainer
              vm={versionsVm.revertVm}
              version={vm.legendVersion?.version ?? 0}
            />
          </>
        }
        testId={testId}
      >
        <LegendEditorTabContainer vm={vm} />
      </LegendEditor>
    )
  },
)

const LegendEditorTabSkeletonContainer = observer(
  function LegendEditorTabSkeletonContainer({
    vm,
  }: LegendEditorContainerProps) {
    switch (vm.headerTab) {
      case 'Questions':
        return <QuestionsTabSkeletonContainer vm={vm} />
      case 'Templates':
        return (
          <SidebarLayout
            sidebar={
              <TemplatesSidebar isEmpty={false}>
                <SkeletonList count={3}>
                  <SidebarMenuItemSkeleton />
                </SkeletonList>
              </TemplatesSidebar>
            }
          />
        )
      case 'Preview':
        return (
          <PreviewLayout
            inputs={
              <PreviewInputsPane>
                <PreviewQuestions isEmpty={false}>
                  <SkeletonList count={6}>
                    <PreviewQuestionRowSkeleton />
                  </SkeletonList>
                </PreviewQuestions>
              </PreviewInputsPane>
            }
            outputs={<PreviewOutputsPane tabs={null} />}
          />
        )
      case 'Options':
        return <OptionsTabSkeletonContainer vm={vm} />
    }
  },
)

const LegendEditorTabContainer = observer(function LegendEditorTabContainer({
  vm,
}: LegendEditorContainerProps) {
  switch (vm.headerTab) {
    case 'Questions':
      return <QuestionsTabContainer vm={vm} />
    case 'Templates':
      return <TemplatesTabContainer vm={vm} />
    case 'Preview':
      return <PreviewTabContainer vm={vm} />
    case 'Options':
      return <OptionsTabContainer vm={vm} />
  }
})

const LegendEditorHeaderContainer = observer(
  function LegendEditorHeaderContainer({ vm }: LegendEditorContainerProps) {
    const versionsVm = vm.versionsVm
    // SAFETY: the editor only renders once a version is loaded.
    const version = vm.legendVersion!
    return (
      <LegendEditorHeader
        isViewingOlderVersion={
          versionsVm.isViewingPreviousVersion &&
          !versionsVm.isViewingLiveVersion
        }
        breadcrumb={
          <LegendEditorBreadcrumbContainer
            vm={vm}
            name={vm.name || 'Untitled legend'}
          />
        }
        versionControls={
          <VersionControls
            version={version.version}
            status={versionsVm.getVersionStatus(version)}
            isDraft={versionsVm.isDraft}
            isPickerOpen={versionsVm.pickerModal.showing}
            onTogglePicker={versionsVm.togglePicker}
            onClosePicker={versionsVm.pickerModal.close}
            picker={<VersionPickerContainer vm={versionsVm} />}
            canDiscard={versionsVm.canDiscardLocalChanges && vm.canModify}
            isDiscardPending={versionsVm.discardLocalChanges.pending}
            onDiscard={versionsVm.discardLocalChanges}
            testId={testId}
          />
        }
        tabs={<LegendEditorTabsContainer vm={vm} />}
        actions={
          <>
            {vm.canModify && (
              <SaveStatus
                isSaving={vm.isSaving}
                text={vm.saveStatusText}
                title={vm.saveLatencyTitle}
              />
            )}
            <PaletteButton
              shortcutLabel={isApplePlatform ? '⌘K' : 'Ctrl K'}
              onPress={vm.paletteModal.show}
              testId={`${testId}-palette-button`}
            />
            {vm.canModify && <PrimaryActionContainer vm={vm} />}
          </>
        }
      />
    )
  },
)

/**
 * Publish for drafts, revert for older versions.
 */
const PrimaryActionContainer = observer(function PrimaryActionContainer({
  vm,
}: LegendEditorContainerProps) {
  const versionsVm = vm.versionsVm
  // SAFETY: the editor only renders once a version is loaded.
  const version = vm.legendVersion!

  if (versionsVm.isViewingPreviousVersion || version.published) {
    return (
      versionsVm.latestVersion !== version && (
        <Button
          label="Revert draft to this"
          size="small"
          variant="primary"
          iconLeft="undo"
          isPending={versionsVm.revertToSelectedVersion.pending}
          isDisabled={vm.isBusy && !versionsVm.revertToSelectedVersion.pending}
          onPress={versionsVm.revertToSelectedVersion}
          testId={`${testId}-revert-button`}
        />
      )
    )
  }

  return (
    <Dropdown
      isOpen={versionsVm.publishModal.showing}
      onButtonPress={versionsVm.togglePublishPanel}
      onRequestClose={
        versionsVm.publish.pending ? undefined : versionsVm.publishModal.close
      }
      position="bottom-end"
      button={
        <Button
          label="Publish"
          size="small"
          variant="primary"
          isDisabled={vm.isBusy}
          testId={`${testId}-publish-button`}
        />
      }
    >
      <PublishPanelContainer vm={vm} />
    </Dropdown>
  )
})

interface LegendEditorBreadcrumbContainerProps extends LegendEditorContainerProps {
  name: ReactNode
}

const LegendEditorBreadcrumbContainer = observer(
  function LegendEditorBreadcrumbContainer({
    vm,
    name,
  }: LegendEditorBreadcrumbContainerProps) {
    return (
      <ViewHeading
        primaryText={<RawButton onPress={vm.showLegendList}>Legends</RawButton>}
        secondaryText={name}
      />
    )
  },
)

interface LegendEditorTabsContainerProps extends LegendEditorContainerProps {
  /**
   * Whether we're rendering before the legend has loaded. Errors are left out
   * until then.
   */
  isSkeleton?: boolean
}

const LegendEditorTabsContainer = observer(function LegendEditorTabsContainer({
  vm,
  isSkeleton,
}: LegendEditorTabsContainerProps) {
  return (
    <Segmented
      size="large"
      options={headerTabs.map(({ tab, title, icon }) => ({
        value: tab,
        label: title,
        icon,
        error: isSkeleton ? undefined : vm.getHeaderTabError(tab),
        testId: `${testId}-tab-${tab.toLowerCase()}`,
      }))}
      value={vm.headerTab}
      onChange={vm.setTab}
      aria-label="Legend editor sections"
    />
  )
})
